"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import {
  LuBell,
  LuDollarSign,
  LuHouse as LuHome,
  LuMail,
  LuMap,
  LuWallet,
} from "react-icons/lu";

import { useIsLoggedIn } from "@/components/shared/is-logged-in";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLayout } from "@/contexts/layout-context";
import {
  useUnreadMessagesSignalState,
  useUnreadNotificationsSignalState,
} from "@/contexts/user-context";
import { useBackgroundPathname } from "@/hooks/use-background-pathname";
import { APP_ROUTES } from "@/utils/constants";
import { abbreviateNumber } from "@/utils/helpers";
import { cn } from "@/utils/lib";

export const MobileNavigation = () => {
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [navClass, setNavClass] = useState("");
  const pathname = useBackgroundPathname();
  const { pwa } = useLayout();

  const { unreadTotalMessagesCount } = useUnreadMessagesSignalState();
  const { unreadNotifications } = useUnreadNotificationsSignalState();
  const isLoggedIn = useIsLoggedIn();

  const navItems = [
    { href: APP_ROUTES.app, label: "Home", Icon: LuHome },
    { href: APP_ROUTES.discover, label: "Discover", Icon: LuMap },
    { href: APP_ROUTES.trade, label: "Trade", Icon: LuDollarSign },
    {
      href: APP_ROUTES.messages,
      label: "Messages",
      Icon: LuMail,
      badge:
        Number(unreadTotalMessagesCount) > 0
          ? abbreviateNumber(Number(unreadTotalMessagesCount))
          : undefined,
      hidden: !isLoggedIn,
    },
    {
      href: APP_ROUTES.notifications,
      label: "Notifications",
      Icon: LuBell,
      badge: unreadNotifications
        ? abbreviateNumber(unreadNotifications)
        : undefined,
      hidden: !isLoggedIn,
    },
    { href: APP_ROUTES.wallet, label: "Wallet", Icon: LuWallet },
  ].filter(({ hidden }) => !hidden);

  const visibleClass = "opacity-100";
  const hiddenClass = "opacity-40";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;

      if (currentScrollPos > lastScrollTop) {
        setNavClass(hiddenClass);
      } else {
        setNavClass(visibleClass);
      }
      setLastScrollTop(currentScrollPos <= 0 ? 0 : currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollTop]);

  return (
    <div
      className={cn(
        navClass,
        `
          mobile-nav-wrapper fixed bottom-0 z-50 flex w-full items-center
          justify-between border-t border-border-light bg-background px-4 pt-2
          pb-[calc(0.5rem-env(safe-area-inset-bottom))] backdrop-blur-xl
          transition-opacity duration-1000
          lg:hidden
        `,
      )}
      onMouseEnter={() => setNavClass(visibleClass)}
      onMouseLeave={() => setNavClass(hiddenClass)}
      onTouchStart={() => setNavClass(visibleClass)}
    >
      {navItems.map(({ href, label, Icon, badge }) => (
        <Link href={href} key={label} className="relative" prefetch={false}>
          <Button
            size="icon"
            variant="outline"
            className={cn("border-none text-muted", {
              "text-primary":
                (href !== "/" && pathname.startsWith(href)) ||
                pathname === href,
            })}
          >
            <Icon
              className="
                text-2xl
                md:text-3xl
              "
            />
          </Button>
          {badge && (
            <Badge
              variant="destructive"
              className="
                absolute -top-1 -right-1 scale-75 border-2 border-background
                px-2 text-xs
              "
            >
              {badge}
            </Badge>
          )}
        </Link>
      ))}

      <style>{`
        @media (max-width: 1023px) {
          ${
            pwa
              ? `html.os-ios {
            --mobile-nav-height: calc(81px + env(safe-area-inset-bottom));
          }
          html.os-ios .mobile-nav-wrapper {
            padding-bottom: calc(2rem - env(safe-area-inset-bottom));
          }`
              : ""
          }

          :root {
            --mobile-nav-height: calc(57px + env(safe-area-inset-bottom));
          }
        }
      `}</style>
    </div>
  );
};
